// householdRole.ts — geteilte Ableitung der konkreten Beziehung aus einer
// Haushaltsrolle (HOUSEHOLD_ROLES) + Geschlecht. Genutzt vom Detail-Dialog
// (RelationCreateDialog) UND der Inline-Anlage (HouseholdList/persistRelations),
// damit beide identisch ableiten. „Was die andere Person für den aktuellen
// Kontakt ist" (z. B. Tochter/Sohn, Mutter/Vater, Ehefrau/Ehemann).

import { reverseKinshipLabel, type HouseholdRoleKey } from "../services/haushalteCleanupService";

/**
 * Geschlecht eines Kontakts auf 'm'/'f'/null normalisieren. Unterstützt beide
 * Shapes: Detail-Kontakt (`salutation.salutation`) und Inline-ContactRef
 * (`salutationLabel`), jeweils plus `gender`.
 */
export function genderOf(contact: any): "m" | "f" | null {
  const g = String(contact?.gender ?? "").toLowerCase();
  const sal = String(contact?.salutation?.salutation ?? contact?.salutationLabel ?? "").toLowerCase();
  if (g.startsWith("m") || sal === "herr") return "m";
  if (g.startsWith("w") || sal === "frau") return "f";
  return null;
}

/**
 * Konkrete Beziehung der gewählten Person zum aktuellen Kontakt — abgeleitet aus
 * der Haushalts-/Beziehungsrolle und dem Geschlecht der Person. „sonstige" nutzt
 * den Freitext; ohne Freitext bzw. bei unbekannter Rolle wird NICHTS abgeleitet
 * (früher „Verwandt" — die Quelle der unbrauchbaren Sammel-Beziehungen).
 */
export function deriveHouseholdRole(roleKey: HouseholdRoleKey, contact: any, freeText?: string): string {
  const g = genderOf(contact);
  const byg = (m: string, f: string, n: string) => (g === "m" ? m : g === "f" ? f : n);
  switch (roleKey) {
    case "ehepartner":
      return byg("Ehemann", "Ehefrau", "Ehepartner(in)");
    case "lebenspartner":
      return byg("Lebenspartner", "Lebenspartnerin", "Lebenspartner(in)");
    case "ex_partner":
      return byg("Ex-Partner", "Ex-Partnerin", "Ex-Partner(in)");
    case "eltern":
      return byg("Vater", "Mutter", "Elternteil");
    case "kind":
      return byg("Sohn", "Tochter", "Kind");
    case "grosseltern":
      return byg("Großvater", "Großmutter", "Großelternteil");
    case "sonstige":
      return (freeText || "").trim();
    default:
      return "";
  }
}

/** Sentinel-Wert der Freitext-Option im Beziehungs-Dropdown (kein echtes Label). */
export const RELATIONSHIP_FREE = "__frei__";

/** Flache, konkrete & gegenderte Beziehungs-Auswahl (Popup + Inline-Anlage). */
export const RELATIONSHIP_CATALOG: string[] = [
  "Ehemann", "Ehefrau",
  "Lebenspartner", "Lebenspartnerin",
  "Ex-Ehemann", "Ex-Ehefrau",
  "Ex-Lebenspartner", "Ex-Lebenspartnerin",
  // „Wenn geschieden, dann Ex-Partner — das ist eine Beziehung bei uns“
  // (User 02.10.2026). Stand schon in RELATIONSHIP_OPTIONS der Bereinigung,
  // fehlte aber in der Auswahl von Anlage und Akte.
  "Ex-Partner", "Ex-Partnerin",
  "Vater", "Mutter",
  "Sohn", "Tochter",
  "Stiefvater", "Stiefmutter",
  "Stiefsohn", "Stieftochter",
  "Schwiegervater", "Schwiegermutter",
  "Schwiegersohn", "Schwiegertochter",
  "Großvater", "Großmutter",
  "Enkel", "Enkelin",
  "Bruder", "Schwester",
  // Halbgeschwister (User 30.09.2026): eigene Labels, damit ein übernommener
  // Vorschlag „Halb- oder Vollgeschwister?" dauerhaft richtig dasteht.
  "Halbbruder", "Halbschwester",
  "Onkel", "Tante",
  "Neffe", "Nichte",
  "Cousin", "Cousine",
  "Schwager", "Schwägerin",
];

/** Ist `v` ein bekanntes Katalog-Label (nicht Freitext/Sentinel)? */
export const isCatalogLabel = (v: string): boolean => RELATIONSHIP_CATALOG.includes(v);

/**
 * Gegenrichtung eines KATALOG-Vorwärts-Labels für die Live-Vorschau — „was self
 * für other ist", abgeleitet aus dem Geschlecht von self. Fehlt es, greift bei
 * Partner-Beziehungen das Gegengeschlecht, sonst die Unisex-Form. Fällt auf das
 * Vorwärts-Label zurück, wenn keine Umkehr bekannt ist.
 */
export function catalogReverseLabel(forward: string, selfGender: "m" | "f" | null): string {
  return reverseKinshipLabel(forward, selfGender, { partnerFallback: "opposite" }) ?? forward;
}

/**
 * Auswählbare Gegenrichtungen zu einem Katalog-Vorwärts-Label — die Antwort auf
 * „was kann self für other sein?", wenn selfs Geschlecht NICHT hinterlegt ist.
 *
 * Hintergrund (gemeldeter Fehler): „Cousin" bei fehlendem Geschlecht ergab über
 * {@link reverseKinshipLabel} die Sammel-Antwort „Verwandt" — fachlich wertlos und
 * seit PR #1091 nicht einmal mehr im Katalog auswählbar. Die Gegenrichtung ist bei
 * JEDEM der 36 Katalog-Labels geschlechtsabhängig; ohne Geschlecht ist sie also
 * grundsätzlich nicht eindeutig. Statt zu raten liefert diese Funktion die echten
 * Kandidaten, aus denen der Nutzer wählt.
 *
 * `[]` = keine Verwandtschafts-Umkehr bekannt (Freitext, Firmen-Rollen) → dort gibt
 * es nichts anzubieten. `length > 1` = nicht eindeutig → es MUSS gefragt werden.
 *
 * Angeboten werden AUSSCHLIESSLICH Katalog-Labels. Der geschlechtslose Aufruf
 * liefert bei den meisten Labels eine Sammelform („Kind", „Elternteil",
 * „Geschwister", „Ehepartner(in)", „Verwandt") — genau die Kategorie, die PR #1091
 * aus dem Bestand entfernt hat; sie zur Auswahl zu stellen würde sie neu erzeugen.
 * Zusätzlich fiele der Bearbeiten-Dialog der Gegenseite bei einem Nicht-Katalog-Wert
 * in den Freitext-Zweig (`isCatalogLabel` in `bezPrefill`) — ein aus einem Dropdown
 * gewählter Wert würde drüben also zu Freitext. Die beiden gegenderten Formen decken
 * jedes Katalog-Label vollständig ab.
 */
export function reverseOptions(forward: string): string[] {
  const cands = [
    reverseKinshipLabel(forward, "m"),
    reverseKinshipLabel(forward, "f"),
  ].filter((x): x is string => !!x && isCatalogLabel(x));
  return [...new Set(cands)];
}
